import { useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

/**
 * The school's event types, from `Dictionary/EventActivityTypes`.
 *
 * ## Why a dictionary and not the constant this replaces
 *
 * These were ten strings hardcoded here, with a note saying to move them the day
 * the school wanted to add one without a release. That day arrived when the API
 * began ROUTING APPROVALS on them: a workflow may only name a type in this
 * catalogue, so a constant in the client would be a second list that could
 * disagree with the one the server validates against, and the failure would be a
 * 422 on a value the screen itself offered.
 *
 * NOT `Dictionary/EventTypes`. That document exists and holds the timetable's
 * vocabulary — Class, Assessments, Cover, Yard Duty — which is a different set
 * of words answering a different question.
 *
 * ## If it comes back empty
 *
 * The seed script is additive and has to be run before the API will accept any
 * `EventTypes` value at all, so an empty answer means an unseeded environment
 * rather than a school with no event types. Callers should say so rather than
 * drawing an empty picker: an empty list here makes an event untypeable and
 * every scoped workflow unsaveable, and neither is obvious from a blank space.
 */

export interface EventActivityType {
	Code: string;
	Name?: string;
	Order?: number;
}

export interface EventTypeCatalogue {
	types: EventActivityType[];
	loaded: boolean;
	failed: boolean;
}

const EMPTY: EventActivityType[] = [];

export function useEventTypes(): EventTypeCatalogue {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery<EventActivityType[]>({
		queryKey: ["dictionary", "EventActivityTypes"],
		queryFn: async () => {
			const res = await callPymbleApi(
				instance,
				"GET",
				"dictionary/EventActivityTypes?dataonly=true",
			);
			/* `dataonly` returns the `Data` array itself, and null when the
			   document is not there — which is the unseeded case, not an error. */
			return Array.isArray(res) ? (res as EventActivityType[]) : [];
		},
		/* The school's vocabulary, not a person's data: it changes when somebody
		   edits a dictionary, which is rare enough that refetching it per mount
		   is pure noise. */
		staleTime: 60 * 60 * 1000,
		enabled: isAuthenticated,
	});

	const types = useMemo(() => {
		const rows = query.data ?? EMPTY;
		/* `Order` is display order only and the API does not promise to sort. A
		   missing one sorts last rather than to the front, where a 0 default
		   would quietly put every unnumbered entry. */
		return [...rows]
			.filter((t) => t?.Code)
			.sort(
				(a, b) =>
					(a.Order ?? Number.MAX_SAFE_INTEGER) -
						(b.Order ?? Number.MAX_SAFE_INTEGER) ||
					(a.Name ?? a.Code).localeCompare(b.Name ?? b.Code, "en-AU"),
			);
	}, [query.data]);

	return {
		types,
		loaded: !query.isPending,
		failed: query.isError,
	};
}
